import React from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DeletionTargetType } from "@/types/data-deletion";

interface DeletionDangerZoneProps {
  targetType: DeletionTargetType;
  description: string;
  buttonLabel: string;
  onDelete: () => void;
  /** A non-terminal job already exists for this target. */
  inFlight?: boolean;
  onViewInFlight?: () => void;
}

export const DeletionDangerZone: React.FC<DeletionDangerZoneProps> = ({
  targetType,
  description,
  buttonLabel,
  onDelete,
  inFlight = false,
  onViewInFlight,
}) => {
  const { t } = useTranslation();
  const targetWord = t(`dataDeletion.targetWord.${targetType}`);

  return (
    <div className="flex flex-col gap-4 rounded-2xl border border-custom-destructive-dark/30 bg-custom-destructive-dark/5 p-6">
      <div className="flex items-center gap-2 text-custom-destructive-dark">
        <AlertTriangle className="size-5" />
        <h2 className="text-lg font-semibold">
          {t("dataDeletion.dangerZone.title")}
        </h2>
      </div>
      <p className="text-sm text-foreground">{description}</p>

      {inFlight ? (
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm font-medium text-custom-destructive-dark">
            {t("dataDeletion.dangerZone.inFlight", { target: targetWord })}
          </p>
          {onViewInFlight ? (
            <Button
              type="button"
              variant="new_outline"
              onClick={onViewInFlight}
            >
              {t("dataDeletion.dangerZone.viewInFlight")}
            </Button>
          ) : null}
        </div>
      ) : (
        <div>
          <Button type="button" variant="new_destructive" onClick={onDelete}>
            {buttonLabel}
          </Button>
        </div>
      )}
    </div>
  );
};
